// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { xScreen } from "@bg/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

export interface GlobalWebsiteMessageStyles {
    container: InjectableCss;
    textStyles: TypographyPresentationProps;
    buttonStyle?: ButtonPresentationProps;
}

export const globalWebsiteMessageStyles: GlobalWebsiteMessageStyles = {
    container: {
        css: css`
            text-align: center;
            position: relative;
            padding: 17px 0;
            background: ${getColor("secondary.main")};
            display: flex;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
        `,
    },
    textStyles: {
        size: fontConfig.fontSize.small,
        weight: fontConfig.weight.semiBold,
        lineHeight: "17px",
        css: css`
            text-align: right;
            margin-bottom: 0;
            letter-spacing: 0.5px;
            & > ${TypographyStyle} {
                font-size: inherit;
                font-weight: inherit;
                line-height: inherit;
                margin-bottom: 0;
            }

            ${xScreen("lte", "xs")} {
                text-align: center;
            }
        `,
    },
    buttonStyle: {
        css: css`
            margin-left: 27px;
            ${xScreen("lte", "xs")} {
                margin-left: 0;
                margin-top: 15px;
            }
        `,
    },
};

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    globalMessage = "globalMessage",
    buttonLabel = "buttonLabel",
    buttonLinkUrl = "buttonLinkUrl",
    buttonVariant = "buttonVariant",
}

type Props = WidgetProps & {
    fields: {
        [fields.globalMessage]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLinkUrl]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
    };
};

const GlobalWebsiteMessage: React.FC<Props> = ({
    fields: { globalMessage, buttonLabel, buttonLinkUrl, buttonVariant },
}) => {
    let url;
    if (buttonLinkUrl) {
        url = useGetLink(buttonLinkUrl)?.url;
    }

    return globalMessage?.length ? (
        <StyledWrapper {...globalWebsiteMessageStyles.container}>
            <Typography {...globalWebsiteMessageStyles.textStyles}>
                {globalMessage && parse(globalMessage, parserOptions)}
            </Typography>
            {url && buttonLabel && (
                <Button {...({ href: url } as any)} variant={buttonVariant} {...globalWebsiteMessageStyles.buttonStyle}>
                    {buttonLabel}
                </Button>
            )}
        </StyledWrapper>
    ) : (
        <></>
    );
};

const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    displayName: "Global Website Message",
    allowedContexts: ["Header", "HomePage"],
    fieldDefinitions: [
        {
            name: fields.globalMessage,
            displayName: "Global Message Content",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "your content goes here",
            isRequired: true,
        },
        {
            name: fields.buttonLabel,
            editorTemplate: "TextField",
            displayName: "Button Label",
            defaultValue: "",
            fieldType: "General",
        },
        {
            name: fields.buttonLinkUrl,
            displayName: "Button Link Url",
            editorTemplate: "LinkField",
            defaultValue: { type: "Url", value: "" },
            fieldType: "General",
        },
        {
            name: fields.buttonVariant,
            displayName: "Button Variant",
            editorTemplate: "DropDownField",
            fieldType: "General",
            options: [
                { displayName: "Primary", value: "primary" },
                { displayName: "Secondary", value: "secondary" },
                { displayName: "Tertiary", value: "tertiary" },
            ],
            hideEmptyOption: true,
            defaultValue: "primary",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: GlobalWebsiteMessage,
    definition,
};

export default widgetModule;
